import type { AzureComponents } from "@p0security/integrations-azure-shared/components";
import {
  P0_SESSION_TERMINATOR_ROLE_DESCRIPTION,
  P0_SESSION_TERMINATOR_ROLE_NAME,
  P0_SESSION_TERMINATOR_ROLE_PERMISSIONS,
} from "@p0security/integrations-azure-shared/permissions";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import type {
  FrontendInstallContext,
  FrontendInstaller,
} from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import { CommandDisplay } from "../CommandDisplay.tsx";

const setupFor = (context: FrontendInstallContext<AzureIntegration>) => {
  const { directoryId } = context.config.root[SINGLETON_ITEM] ?? {};
  // Assigned by the jump-host assembler when the component is added; the
  // customer's app registration federates this service account's identity.
  const { serviceAccountId } =
    context.config["jump-host"]?.[SINGLETON_ITEM] ?? {};
  if (!directoryId || !serviceAccountId) {
    throw new Error("Azure tenant is not configured.");
  }
  // Storage account and Function App names must be globally unique (storage
  // accounts additionally allow only 3-24 lowercase alphanumeric characters),
  // so both carry a suffix derived from the tenant ID variable/local that each
  // example defines up front — the tenant ID itself is never rendered.
  return {
    serviceAccountId,
    appName: "p0-session-terminator",
    resourceGroupName: "p0-security-jump-host-connector-rg",
    location: "westus2",
    storageAccountBaseName: "p0terminator",
    servicePlanName: "p0-security-jump-host-connector-plan",
    functionAppBaseName: "p0-security-jump-host-connector",
    connectorImage: {
      registryUrl: "https://index.docker.io",
      name: "p0security/p0-security-perimeter-azure-vm",
      tag: "latest",
    },
    credentialInfo: {
      name: "gcp-workload-identity",
      issuer: "https://accounts.google.com",
      audiences: ["api://AzureADTokenExchange"],
    },
    customRole: {
      name: P0_SESSION_TERMINATOR_ROLE_NAME,
      description: P0_SESSION_TERMINATOR_ROLE_DESCRIPTION,
      actions: P0_SESSION_TERMINATOR_ROLE_PERMISSIONS,
      isCustom: true,
    },
  } as const;
};

const azureJumpHostInstructionsFor = (
  context: FrontendInstallContext<AzureIntegration>
): Instructions<ReactNode> => {
  const {
    serviceAccountId,
    appName,
    resourceGroupName,
    location,
    storageAccountBaseName,
    servicePlanName,
    functionAppBaseName,
    connectorImage,
    credentialInfo,
    customRole,
  } = setupFor(context);
  const { issuer, audiences, name: credentialName } = credentialInfo;
  const transformedActions = JSON.stringify(customRole.actions, null, 2);

  const shell = [
    {
      header:
        "Target the subscription that will host the connector, and set your tenant ID. tenantPrefix is the tenant-derived suffix that disambiguates globally-unique resource names.",
      command: `az account set --subscription "<subscription-id>"

tenantId="<tenant-id>"
tenantPrefix="\${tenantId:0:8}"`,
    },
    {
      header:
        "Create the connector resource group, storage account, and App Service plan",
      command: `az group create --name ${resourceGroupName} --location ${location}

az storage account create \\
  --name ${storageAccountBaseName}$tenantPrefix \\
  --resource-group ${resourceGroupName} \\
  --location ${location} \\
  --sku Standard_LRS

az functionapp plan create \\
  --name ${servicePlanName} \\
  --resource-group ${resourceGroupName} \\
  --location ${location} \\
  --is-linux true \\
  --sku EP1`,
    },
    {
      header:
        "Create the session terminator app registration and federated credential",
      command: `appId=$(az ad app create --display-name "${appName}" --query appId -o tsv)
az ad sp create --id "$appId"

az ad app update --id "$appId" --set api.requestedAccessTokenVersion=2
az ad app update --id "$appId" --identifier-uris "api://$appId"

cat > federated-credential.json << EOF
{
  "name": "${credentialName}",
  "issuer": "${issuer}",
  "subject": "${serviceAccountId}",
  "audiences": ["${audiences.join('", "')}"]
}
EOF
az ad app federated-credential create --id "$appId" --parameters federated-credential.json`,
    },
    {
      header: "Deploy the connector Function App",
      command: `az functionapp create \\
  --name ${functionAppBaseName}-$tenantPrefix \\
  --resource-group ${resourceGroupName} \\
  --plan ${servicePlanName} \\
  --storage-account ${storageAccountBaseName}$tenantPrefix \\
  --functions-version 4 \\
  --image index.docker.io/${connectorImage.name}:${connectorImage.tag} \\
  --assign-identity "[system]"

az functionapp config appsettings set \\
  --name ${functionAppBaseName}-$tenantPrefix \\
  --resource-group ${resourceGroupName} \\
  --settings \\
    FUNCTIONS_WORKER_RUNTIME=node \\
    "CALLER_APP_ID=$appId" \\
    LOG_SECURITY_EVENTS=true \\
    WEBSITES_ENABLE_APP_SERVICE_STORAGE=false \\
    AzureWebJobsFeatureFlags=EnableWorkerIndexing

cat > auth-settings.json << EOF
{
  "platform": { "enabled": true },
  "globalValidation": {
    "requireAuthentication": true,
    "unauthenticatedClientAction": "Return401"
  },
  "identityProviders": {
    "azureActiveDirectory": {
      "enabled": true,
      "registration": {
        "clientId": "$appId",
        "openIdIssuer": "https://login.microsoftonline.com/$tenantId/v2.0"
      },
      "validation": {
        "allowedAudiences": ["$appId"],
        "defaultAuthorizationPolicy": { "allowedApplications": ["$appId"] }
      }
    }
  },
  "login": {}
}
EOF
az webapp auth set \\
  --name ${functionAppBaseName}-$tenantPrefix \\
  --resource-group ${resourceGroupName} \\
  --body @auth-settings.json`,
    },
    {
      header:
        "Create the Session Terminator role and assign it to the connector",
      command: `cat > p0-session-terminator-role.json << EOF
{
  "Name": "${customRole.name}",
  "IsCustom": ${customRole.isCustom},
  "Description": "${customRole.description}",
  "Actions": ${transformedActions},
  "NotActions": [],
  "AssignableScopes": ["<management-group-id>"]
}
EOF
az role definition create --role-definition p0-session-terminator-role.json

principalId=$(az functionapp identity show \\
  --name ${functionAppBaseName}-$tenantPrefix \\
  --resource-group ${resourceGroupName} \\
  --query principalId -o tsv)
az role assignment create \\
  --assignee-object-id "$principalId" \\
  --assignee-principal-type ServicePrincipal \\
  --role "${customRole.name}" \\
  --scope "<jump-host-vm-resource-id>"`,
    },
  ];

  const iac = [
    {
      header:
        "Define the connector locals and Terraform providers (skip the provider blocks if your workspace already configures azurerm and azuread)",
      command: `locals {
  # The subscription that hosts the connector.
  subscription_id = "<subscription-id>"
  # Your Azure tenant (Entra directory) ID.
  tenant_id = "<tenant-id>"
  # The tenant-derived suffix that disambiguates globally-unique resource names.
  tenant_prefix = lower(substr(local.tenant_id, 0, 8))
}

terraform {
  required_version = ">= 1.5"

  required_providers {
    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~> 4.0"
    }
    azuread = {
      source  = "hashicorp/azuread"
      version = "~> 3.0"
    }
  }
}

provider "azurerm" {
  features {}

  subscription_id = local.subscription_id
  tenant_id       = local.tenant_id
}

provider "azuread" {
  tenant_id = local.tenant_id
}`,
    },
    {
      header:
        "Create the connector resource group, storage account, and App Service plan",
      command: `resource "azurerm_resource_group" "connector" {
  name     = "${resourceGroupName}"
  location = "${location}"
}

resource "azurerm_storage_account" "connector" {
  name                     = "${storageAccountBaseName}\${local.tenant_prefix}"
  resource_group_name      = azurerm_resource_group.connector.name
  location                 = azurerm_resource_group.connector.location
  account_tier             = "Standard"
  account_replication_type = "LRS"
}

resource "azurerm_service_plan" "connector" {
  name                = "${servicePlanName}"
  resource_group_name = azurerm_resource_group.connector.name
  location            = azurerm_resource_group.connector.location
  os_type             = "Linux"
  sku_name            = "EP1"
}`,
    },
    {
      header:
        "Create the session terminator app registration and federated credential",
      command: `resource "azuread_application" "function" {
  display_name = "${appName}"

  api {
    requested_access_token_version = 2
  }

  lifecycle {
    ignore_changes = [identifier_uris]
  }
}

resource "azuread_service_principal" "function" {
  client_id = azuread_application.function.client_id
}

resource "azuread_application_identifier_uri" "function" {
  application_id = azuread_application.function.id
  identifier_uri = "api://\${azuread_application.function.client_id}"
}

resource "azuread_application_federated_identity_credential" "function" {
  application_id = azuread_application.function.id
  display_name   = "${credentialName}"
  issuer         = "${issuer}"
  subject        = "${serviceAccountId}"
  audiences      = ["${audiences.join('", "')}"]
}`,
    },
    {
      header: "Deploy the connector Function App",
      command: `resource "azurerm_linux_function_app" "connector" {
  name                = "${functionAppBaseName}-\${local.tenant_prefix}"
  resource_group_name = azurerm_resource_group.connector.name
  location            = azurerm_resource_group.connector.location
  service_plan_id     = azurerm_service_plan.connector.id

  storage_account_name       = azurerm_storage_account.connector.name
  storage_account_access_key = azurerm_storage_account.connector.primary_access_key

  functions_extension_version = "~4"

  identity {
    type = "SystemAssigned"
  }

  site_config {
    application_stack {
      docker {
        registry_url = "${connectorImage.registryUrl}"
        image_name   = "${connectorImage.name}"
        image_tag    = "${connectorImage.tag}"
      }
    }
  }

  app_settings = {
    FUNCTIONS_WORKER_RUNTIME = "node"
    CALLER_APP_ID            = azuread_application.function.client_id
    LOG_SECURITY_EVENTS      = "true"

    WEBSITES_ENABLE_APP_SERVICE_STORAGE = "false"

    AzureWebJobsFeatureFlags = "EnableWorkerIndexing"
  }

  auth_settings_v2 {
    auth_enabled           = true
    require_authentication = true
    unauthenticated_action = "Return401"

    active_directory_v2 {
      client_id            = azuread_application.function.client_id
      tenant_auth_endpoint = "https://login.microsoftonline.com/\${local.tenant_id}/v2.0"
      allowed_audiences    = [azuread_application.function.client_id]
      allowed_applications = [azuread_application.function.client_id]
    }

    login {}
  }
}`,
    },
    {
      header:
        "Create the Session Terminator role and assign it to the connector",
      command: `resource "azurerm_role_definition" "session_terminator" {
  name        = "${customRole.name}"
  scope       = "<management-group-id>"
  description = "${customRole.description}"

  permissions {
    actions = ${transformedActions}
  }

  assignable_scopes = ["<management-group-id>"]
}

resource "azurerm_role_assignment" "session_terminator_jump_host_1" {
  scope                            = "<jump-host-vm-resource-id>"
  role_definition_id               = azurerm_role_definition.session_terminator.role_definition_resource_id
  principal_id                     = azurerm_linux_function_app.connector.identity[0].principal_id
  skip_service_principal_aad_check = true
}

output "jump_host_management_client_id" {
  description = "Client ID of the session terminator app registration."
  value       = azuread_application.function.client_id
}

output "jump_host_management_function_app_resource_id" {
  description = "Resource ID of the connector Function App."
  value       = azurerm_linux_function_app.connector.id
}`,
    },
  ];

  return {
    help: (
      <>
        <Typography.Paragraph>
          P0 sends privileged commands (e.g. terminating a live SSH session) to
          your jump hosts through a connector: an Azure Function App running
          P0&apos;s connector image, protected by an app registration that P0
          authenticates to using workload identity federation. The connector
          runs commands on your jump host VMs with a least-privilege custom
          role, scoped to those VMs only.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Run the following to create the connector. Replace{" "}
          <Typography.Text code>&lt;tenant-id&gt;</Typography.Text> (your Azure
          tenant ID),{" "}
          <Typography.Text code>&lt;subscription-id&gt;</Typography.Text> (the
          subscription that hosts the connector),{" "}
          <Typography.Text code>&lt;management-group-id&gt;</Typography.Text>{" "}
          (the management group containing your jump-host subscriptions), and{" "}
          <Typography.Text code>
            &lt;jump-host-vm-resource-id&gt;
          </Typography.Text>{" "}
          (the resource ID of each jump host VM) with your values.
        </Typography.Paragraph>
      </>
    ),
    commands: {
      shell,
      iac,
    },
  };
};

export const azureJumpHostInstaller: FrontendInstaller<
  typeof AzureComponents
>["components"]["jump-host"] = {
  instructions: azureJumpHostInstructionsFor,
  items: {
    clientId: {
      labeler: (context) => {
        const { appName } = setupFor(context);
        return (
          <CommandDisplay
            commands={`az ad app list --display-name "${appName}" --query "[0].appId" -o tsv`}
          />
        );
      },
    },
    functionAppResourceId: {
      labeler: (context) => {
        const { resourceGroupName } = setupFor(context);
        return (
          <CommandDisplay
            commands={`az functionapp list --resource-group ${resourceGroupName} --query "[0].id" -o tsv`}
          />
        );
      },
    },
  },
};
